import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { trpc } from '../trpc'
import type { RouterOutputs } from '../trpc'
import {
  parseStoreRoute,
  rememberLastUsedStoreId,
  resolveStoreByCode,
  storeCodeSegment,
} from '../lib/storeRouting'
import { DEFAULT_STORE_PAGE, storePagePath } from '../lib/routes'

export type StoreItem = RouterOutputs['store']['list'][number]

/** One store's ordering-week proposal status, shown by the selector's indicator. */
export type StoreWeekStatusItem = RouterOutputs['orders']['accessibleStoresWeekStatus'][number]

type StoreContextValue = {
  /** The store resolved from the URL's storeCode, or undefined while resolving. */
  activeStore: StoreItem | undefined
  /** All stores available to the current user */
  stores: Array<StoreItem>
  /** Whether stores are still loading from the server */
  isLoading: boolean
  /** Switch store by navigating to the current page under the new store's code. */
  switchStore: (storeId: number) => void
  /** Ordering-week status by storeId, for the store selector's per-store indicator. */
  storeStatus: Record<number, StoreWeekStatusItem>
}

const StoreContext = createContext<StoreContextValue | undefined>(undefined)

type StoreProviderProps = {
  children: React.ReactNode
}

/**
 * Fetches the client's stores and derives the active store FROM THE URL — the
 * store code is part of the route (`/propuesta/:storeCode`), so it is
 * deep-linkable and navigable with back/forward. Must be rendered inside the
 * authenticated area (after RequireAuth) so trpc.store.list only fires when a
 * valid session exists, and under the router so useLocation/useNavigate work.
 *
 * There is no local "active store" state: the URL is the source of truth.
 * localStorage keeps only the LAST-USED store id, so `/` (and the bare
 * `/propuesta` / `/pedidos`) can redirect back to it (see RequireStore).
 */
export const StoreProvider = ({ children }: StoreProviderProps): React.ReactElement => {
  const { data: stores = [], isLoading } = trpc.store.list.useQuery({})
  const location = useLocation()
  const navigate = useNavigate()

  // Ordering-week status per store, for the selector's indicator — the week
  // each store is ordering FOR, which the BACKEND resolves store by store: a
  // store with a cutoff of its own can be ordering for a different week than
  // its neighbours at the same instant, so no single week is asked for here.
  const { data: weekStatuses = [] } = trpc.orders.accessibleStoresWeekStatus.useQuery({})
  const storeStatus = useMemo(() => {
    const byStoreId: Record<number, StoreWeekStatusItem> = {}
    for (const status of weekStatuses) byStoreId[status.storeId] = status
    return byStoreId
  }, [weekStatuses])

  const { storeCode } = parseStoreRoute(location.pathname)
  const activeStore = useMemo(
    () => resolveStoreByCode(stores, storeCode),
    [stores, storeCode],
  )

  // Remember the store currently in view as "last used", purely so `/` can
  // redirect back to it next time. No effect on the active store itself.
  useEffect(() => {
    if (activeStore !== undefined) rememberLastUsedStoreId(activeStore.id)
  }, [activeStore])

  // Switching store is a route navigation: same page, new store code — dropping
  // any deeper segment (e.g. an orderId, which belongs to the previous store).
  // Because it's a navigation, the shell's unsaved-changes blocker covers it
  // uniformly, so no separate store-switch confirm is needed.
  const switchStore = useCallback(
    (storeId: number) => {
      if (storeId === activeStore?.id) return
      const target = stores.find((store) => store.id === storeId)
      if (target === undefined) return
      const { page } = parseStoreRoute(location.pathname)
      navigate(storePagePath(page ?? DEFAULT_STORE_PAGE, storeCodeSegment(target)))
    },
    [stores, activeStore, location.pathname, navigate],
  )

  const value = useMemo(
    () => ({ activeStore, stores, isLoading, switchStore, storeStatus }),
    [activeStore, stores, isLoading, switchStore, storeStatus],
  )

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

/**
 * Returns the active-store context. Must be called inside <StoreProvider>.
 */
export const useActiveStore = (): StoreContextValue => {
  const ctx = useContext(StoreContext)
  if (ctx === undefined) {
    throw new Error('useActiveStore must be used within a StoreProvider')
  }
  return ctx
}
